import { icon } from "./icons";
import { applyStaticTranslations, i18n } from "./i18n";
import { savedTrips } from "./saved-trips-store";
import { stops } from "./stops";
import type { TripState } from "./types";

type Elements = {
  dialog: HTMLDialogElement;
  list: HTMLUListElement;
  empty: HTMLElement;
  closeBtn: HTMLButtonElement;
  closeX: HTMLButtonElement;
  newBtn: HTMLButtonElement;
};

let els: Elements | null = null;

export function initSavedTripsDialog(): void {
  const dialog = required<HTMLDialogElement>("#saved-trips-dialog");
  els = {
    dialog,
    list: required<HTMLUListElement>("#saved-trips-list"),
    empty: required<HTMLElement>("#saved-trips-empty"),
    closeBtn: required<HTMLButtonElement>("#saved-trips-close"),
    closeX: required<HTMLButtonElement>("#saved-trips-close-x"),
    newBtn: required<HTMLButtonElement>("#saved-trips-new"),
  };

  els.closeBtn.addEventListener("click", () => dialog.close());
  els.closeX.addEventListener("click", () => dialog.close());
  els.newBtn.addEventListener("click", () => {
    stops.newTrip();
    dialog.close();
  });
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });

  // Keep the list live while open (a delete, a new trip, or a rename should reflect at once).
  savedTrips.addEventListener("change", () => {
    if (dialog.open) render();
  });
  i18n.addEventListener("change", () => {
    if (dialog.open) render();
  });
}

export function openSavedTripsDialog(): void {
  if (!els) return;
  render();
  applyStaticTranslations(els.dialog);
  els.dialog.showModal();
}

function render(): void {
  if (!els) return;
  const activeId = stops.getState().id;
  const trips = [...savedTrips.list()].sort((a, b) =>
    (b.createdAt ?? "").localeCompare(a.createdAt ?? ""),
  );

  els.list.replaceChildren();
  els.empty.hidden = trips.length > 0;

  for (const trip of trips) {
    els.list.appendChild(buildRow(trip, trip.id === activeId));
  }
}

function buildRow(trip: TripState, isActive: boolean): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "place-item saved-trip-item";
  li.dataset.id = trip.id;
  if (isActive) li.classList.add("is-active");

  const info = document.createElement("div");
  info.className = "saved-trip-info";

  const nameRow = document.createElement("div");
  nameRow.className = "saved-trip-name-row";
  const name = document.createElement("span");
  name.className = "place-item-name";
  name.textContent = trip.tripName?.trim() || i18n.t("savedTripsUntitled");
  nameRow.appendChild(name);
  if (isActive) {
    const badge = document.createElement("span");
    badge.className = "saved-trip-badge";
    badge.textContent = i18n.t("savedTripsActive");
    nameRow.appendChild(badge);
  }

  const meta = document.createElement("span");
  meta.className = "place-item-coords";
  meta.textContent = `${formatDate(trip.createdAt)} · ${stopCountText(trip.stops.length)}`;

  info.append(nameRow, meta);
  li.appendChild(info);

  const actions = document.createElement("div");
  actions.className = "saved-trip-actions";

  if (!isActive) {
    const openBtn = document.createElement("button");
    openBtn.type = "button";
    openBtn.className = "saved-trip-open";
    openBtn.title = i18n.t("savedTripsOpen");
    openBtn.setAttribute("aria-label", i18n.t("savedTripsOpen"));
    openBtn.appendChild(icon("arrow-right", { size: 18 }));
    openBtn.addEventListener("click", () => {
      const full = savedTrips.get(trip.id);
      if (full) stops.loadState(full);
      els?.dialog.close();
    });

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "place-item-remove";
    removeBtn.title = i18n.t("savedTripsDelete");
    removeBtn.setAttribute("aria-label", i18n.t("savedTripsDelete"));
    removeBtn.appendChild(icon("trash", { size: 16 }));
    removeBtn.addEventListener("click", () => {
      const label = trip.tripName?.trim() || i18n.t("savedTripsUntitled");
      if (confirm(i18n.t("savedTripsDeleteConfirm", { name: label }))) {
        savedTrips.remove(trip.id);
      }
    });

    actions.append(openBtn, removeBtn);
  }

  li.appendChild(actions);
  return li;
}

function stopCountText(n: number): string {
  return n === 1 ? i18n.t("savedTripsStopCountOne") : i18n.t("savedTripsStopCount", { n });
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(i18n.current(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function required<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Element not found: ${selector}`);
  return el;
}
